import { useEffect, useMemo, useState } from 'react';

import {
  useAppSelector,
  effectiveLabelStyleSelector,
  LabelStyle,
  LabelType,
} from '../../../../store';

import {
  LabelEditorStyleState,
  resolveLabelEditorStyleState,
} from './editLabelStyleState';

interface UseEditLabelStyleStateParams {
  marker: LabelType;
  id: string;
}

export function useEditLabelStyleState({
  marker,
  id,
}: UseEditLabelStyleStateParams) {
  const getEffectiveStyle = useAppSelector(effectiveLabelStyleSelector);
  const effectiveStyle = getEffectiveStyle(marker);
  const initialStyleState = resolveLabelEditorStyleState(effectiveStyle);

  const [fontSize, setFontSize] = useState(initialStyleState.fontSize);
  const [fontWeight, setFontWeight] = useState<'normal' | 'bold'>(initialStyleState.fontWeight);
  const [color, setColor] = useState(initialStyleState.color);
  const [backgroundColor, setBackgroundColor] = useState(initialStyleState.backgroundColor);
  const [backgroundOpacity, setBackgroundOpacity] = useState(initialStyleState.backgroundOpacity);
  const [borderColor, setBorderColor] = useState(initialStyleState.borderColor);
  const [padding, setPadding] = useState(initialStyleState.padding);
  const [zIndex, setZIndex] = useState(initialStyleState.zIndex);
  const [skewX, setSkewX] = useState(initialStyleState.skewX);
  const [skewY, setSkewY] = useState(initialStyleState.skewY);
  const [perspectiveX, setPerspectiveX] = useState(initialStyleState.perspectiveX);
  const [perspectiveY, setPerspectiveY] = useState(initialStyleState.perspectiveY);
  const [variant, setVariant] = useState<LabelStyle['variant']>(initialStyleState.variant);
  const [rotation, setRotation] = useState<number>();

  const applyStyleState = (nextStyleState: LabelEditorStyleState) => {
    setFontSize(nextStyleState.fontSize);
    setFontWeight(nextStyleState.fontWeight);
    setColor(nextStyleState.color);
    setBackgroundColor(nextStyleState.backgroundColor);
    setBackgroundOpacity(nextStyleState.backgroundOpacity);
    setBorderColor(nextStyleState.borderColor);
    setPadding(nextStyleState.padding);
    setZIndex(nextStyleState.zIndex);
    setSkewX(nextStyleState.skewX);
    setSkewY(nextStyleState.skewY);
    setPerspectiveX(nextStyleState.perspectiveX);
    setPerspectiveY(nextStyleState.perspectiveY);
    setVariant(nextStyleState.variant);
  };

  useEffect(() => {
    const nextStyleState = resolveLabelEditorStyleState(getEffectiveStyle(marker));
    applyStyleState(nextStyleState);
  }, [marker, getEffectiveStyle, id]);

  const currentStyleState = useMemo<LabelEditorStyleState>(() => ({
    fontSize,
    fontWeight,
    color,
    backgroundColor,
    backgroundOpacity,
    borderColor,
    padding,
    zIndex,
    variant,
    skewX,
    skewY,
    perspectiveX,
    perspectiveY,
  }), [
    backgroundColor, backgroundOpacity, borderColor, color, fontSize, fontWeight, padding, variant, zIndex,
    skewX, skewY, perspectiveX, perspectiveY,
  ]);

  return {
    currentStyleState,
    applyStyleState,
    setFontSize,
    setFontWeight,
    setColor,
    setBackgroundColor,
    setBackgroundOpacity,
    setBorderColor,
    setPadding,
    setZIndex,
    setSkewX,
    setSkewY,
    setPerspectiveX,
    setPerspectiveY,
    setVariant,
    rotation,
    setRotation,
  };
}

export default useEditLabelStyleState;
